import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { IVideo, VideoType } from "types";
import ReactPlayer from "react-player";
import { parse, stringify } from "query-string";
import { omit } from "lodash-es";

import { GallerySlide, SlideProps } from "components";

interface VideoSlideProps extends IVideo, SlideProps {}

export const VideoSlide: React.FC<VideoSlideProps> = ({
  url,
  iframe,
  isActive,
  type,
  ...rest
}) => {
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef<ReactPlayer | null>(null);
  const cleanURL = useMemo(() => {
    if (!url) return;

    if (type === VideoType.vimeo) return url;
    const parsedURL = new URL(url);
    const cleanParams = omit(parse(parsedURL.search), ["index", "list"]);

    return `${parsedURL.origin}${parsedURL.pathname}?${stringify(cleanParams)}`;
  }, [url, type]);

  useEffect(() => {
    if (!isActive) {
      setPlaying(false);
    }

    if (isActive) {
      setPlayerLoaded(true);
    }
  }, [isActive]);

  const handlePlayerReady = useCallback(() => {
    document.querySelectorAll("iframe").forEach((iframe) => {
      try {
        const src = new URL(iframe.src);

        if (
          src.searchParams.get("keyboard") !== "false" ||
          src.searchParams.get("disablekb") !== "1"
        ) {
          setPlayerLoaded(true);
          src.searchParams.set("keyboard", "false");
          src.searchParams.set("disablekb", "1");
          iframe.src = src.toString();
          iframe.setAttribute("disablekb", "1");
        }
      } catch (error) {
        console.error("Invalid URL:", iframe.src);
      }
    });
  }, []);

  if (!isActive && !playerLoaded) return <></>;

  const media = (
    <div className="gallery-slide__media">
      {iframe ? (
        <div
          className="gallery-slide__iframe"
          dangerouslySetInnerHTML={{ __html: iframe }}
        />
      ) : (
        <ReactPlayer
          ref={iframeRef}
          playing={playing}
          url={cleanURL}
          onReady={() => handlePlayerReady()}
          controls
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          config={{
            youtube: {
              embedOptions: {
                host: "https://www.youtube-nocookie.com",
              },
            },
          }}
          width="100%"
          height="100%"
        />
      )}
    </div>
  );

  return <GallerySlide media={media} isActive={isActive} {...rest} />;
};
